// Teletel: the product ships in Arabic and English ONLY, and each language is
// loaded on demand.
//
// Upstream Chatwoot imported all 41 dashboard languages here, which put ~8.6 MB
// of translations into the shared entry chunk every user had to download before
// the app could boot. The other locale folders were deleted on 2026-10-11
// (sealed step 6); bringing a language back means restoring its folder, adding
// it to LOADERS and enabling it in config/initializers/languages.rb — the
// single source of truth the
// backend uses for `enabledLanguages`, the account/user language pickers and
// `I18n.available_locales`.
//
// Since 2026-09-04 the two bundled languages are separate lazy chunks: the
// entrypoints load the user's language before mounting, then warm the other
// one in the background. Switching stays a SYNCHRONOUS assignment whenever the
// target language is already loaded — an asynchronous switch landing in the
// middle of a route transition corrupted the component tree under rapid
// navigation (measured 2026-09-03), so `switchLocale` only awaits when the
// language has genuinely never been fetched (first visit, before mount).
export const ALLOWED_LOCALES = ['ar', 'en'];

const LOADERS = {
  ar: () => import('./locale/ar'),
  en: () => import('./locale/en'),
};

export const isSupportedLocale = locale => ALLOWED_LOCALES.includes(locale);

// The dashboard HTML is requested with the SPA's token cookies, not a Devise
// session, so the server cannot see the user's saved language when it renders
// the shell. Remember the language the app settled on in a plain cookie; the
// Teletel dashboard controller reads it to set <html lang dir> and the initial
// i18n locale, so the next load starts in the right language and direction.
export const LOCALE_COOKIE = 'teletel_locale';

export const rememberLocale = locale => {
  if (!isSupportedLocale(locale) || typeof document === 'undefined') return;
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=31536000; SameSite=Lax`;
};

let composer = null;
const loaded = new Set();
const inflight = new Map();

// Called once by each entrypoint with `i18n.global` (composition-mode composer).
export const registerI18n = globalComposer => {
  composer = globalComposer;
};

export const isLocaleLoaded = locale => loaded.has(locale);

// Translate outside a component's setup (plain helpers, composables that
// are also called from tests). Falls back to the key before an entrypoint
// has registered the composer.
export const translate = (key, params) =>
  composer ? composer.t(key, params) : key;

export const loadLocaleMessages = async locale => {
  if (!isSupportedLocale(locale)) return null;
  if (loaded.has(locale)) return composer?.getLocaleMessage(locale) ?? null;
  if (!inflight.has(locale)) {
    inflight.set(
      locale,
      LOADERS[locale]()
        .then(module => {
          const messages = module.default;
          if (composer) composer.setLocaleMessage(locale, messages);
          loaded.add(locale);
          return messages;
        })
        .finally(() => inflight.delete(locale))
    );
  }
  return inflight.get(locale);
};

const applyLocale = locale => {
  if (!composer) return;
  // `locale` is a ref on the composition-mode composer; components may also
  // hold the exported composer where it is a plain property.
  if (composer.locale && typeof composer.locale === 'object') {
    composer.locale.value = locale;
  } else {
    composer.locale = locale;
  }
  rememberLocale(locale);
};

// Switch the UI language. Synchronous when the language is already loaded
// (the normal case after boot); otherwise loads it first. Always returns a
// promise so callers can await the first switch before mounting.
export const switchLocale = locale => {
  if (!isSupportedLocale(locale)) return Promise.resolve(false);
  if (loaded.has(locale)) {
    applyLocale(locale);
    return Promise.resolve(true);
  }
  return loadLocaleMessages(locale).then(messages => {
    if (!messages) return false;
    applyLocale(locale);
    return true;
  });
};

// Warm the other language after mount so a later switch is synchronous.
export const preloadOtherLocales = current => {
  const others = ALLOWED_LOCALES.filter(locale => locale !== current);
  const run = () => others.forEach(locale => loadLocaleMessages(locale));
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(run, { timeout: 5000 });
  } else {
    setTimeout(run, 2000);
  }
};
